'use client'

import { useEffect, useState } from 'react'

import Image from 'next/image'

import Autoplay from 'embla-carousel-autoplay'
import { ArrowRightIcon } from 'lucide-react'

import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel'
import type { CarouselApi } from '@/components/ui/carousel'
import { resolveImageSrc } from '@/lib/image-src'
import { cn } from '@/lib/utils'
import type { Section } from '@/types/website'
import { SITE_CONTAINER } from '@/components/layout/container'
import { PillButton } from '@/components/blocks/pill-button'

type Slide = {
  image?: string | null
  eyebrow?: string | null
  heading?: string | null
  subheading?: string | null
  cta_label?: string | null
  cta_url?: string | null
}

/**
 * Бүтэн дэлгэцийн үндсэн слайдер.
 *
 * Слайдууд нь блокийн `items`-ээс ирнэ. Нэг ч слайд байхгүй бол блокийн
 * үндсэн гарчгийг ганц слайд болгон харуулна.
 */
export const SectionHero = ({ section }: { section: Section }) => {
  const [api, setApi] = useState<CarouselApi>()
  const [current, setCurrent] = useState(0)

  const slides = (section.items as Slide[]).length
    ? (section.items as Slide[])
    : [
        {
          eyebrow: section.eyebrow,
          heading: section.heading,
          subheading: section.subheading,
          cta_label: section.cta.label,
          cta_url: section.cta.url
        }
      ]

  const interval = Number(section.settings.interval ?? 6000)
  const autoplay = section.settings.autoplay !== false

  useEffect(() => {
    if (!api) return

    const onSelect = () => setCurrent(api.selectedScrollSnap())

    api.on('select', onSelect)
    onSelect()

    return () => {
      api.off('select', onSelect)
    }
  }, [api])

  return (
    <section className='relative'>
      <Carousel
        setApi={setApi}
        opts={{ loop: section.settings.loop !== false, align: 'start' }}
        plugins={autoplay ? [Autoplay({ delay: interval, stopOnInteraction: false })] : []}
      >
        <CarouselContent className='ml-0'>
          {slides.map((slide, i) => {
            const image = resolveImageSrc(slide.image)

            return (
              <CarouselItem key={i} className='relative h-svh p-0'>
                {image ? (
                  <Image src={image} alt={slide.heading ?? ''} fill priority={i === 0} className='object-cover' />
                ) : (
                  <div className='from-muted via-background to-muted absolute inset-0 bg-gradient-to-br' />
                )}

                {image && <div className='absolute inset-0 bg-black/45' />}

                <div className={cn(SITE_CONTAINER, 'relative flex h-full flex-col justify-center gap-6')}>
                  {slide.eyebrow && (
                    <span
                      className={cn(
                        'text-xl font-medium tracking-widest uppercase',
                        image ? 'text-white/80' : 'text-muted-foreground'
                      )}
                    >
                      {slide.eyebrow}
                    </span>
                  )}

                  {slide.heading && (
                    <h1
                      className={cn(
                        'max-w-4xl text-4xl font-semibold tracking-tight text-balance md:text-6xl lg:text-7xl',
                        image && 'text-white'
                      )}
                    >
                      {slide.heading}
                    </h1>
                  )}

                  {slide.subheading && (
                    <p
                      className={cn(
                        'max-w-2xl text-xl text-pretty md:text-xl',
                        image ? 'text-white/90' : 'text-muted-foreground'
                      )}
                    >
                      {slide.subheading}
                    </p>
                  )}

                  {slide.cta_url && slide.cta_label && (
                    <PillButton href={slide.cta_url} className='group mt-2 w-fit'>
                      {slide.cta_label}
                      <ArrowRightIcon className='size-4 transition-transform group-hover:translate-x-1' />
                    </PillButton>
                  )}
                </div>
              </CarouselItem>
            )
          })}
        </CarouselContent>

        {slides.length > 1 && (
          <>
            <div className='absolute bottom-10 left-1/2 flex -translate-x-1/2 items-center gap-2'>
              {slides.map((_, i) => (
                <button
                  key={i}
                  type='button'
                  aria-label={`Слайд ${i + 1}`}
                  onClick={() => api?.scrollTo(i)}
                  className={cn(
                    'h-1.5 rounded-full transition-all',
                    i === current ? 'bg-foreground w-8' : 'bg-foreground/30 w-4'
                  )}
                />
              ))}
            </div>
          </>
        )}
      </Carousel>
    </section>
  )
}
